/**
 * Weirwood
 * Copyright 2019 Baidu Inc. All rights reserved.
 *
 * @file 检测浏览器是否支持各种能力
 * @author zengxiaohui(zengxiaohui@baidu.com)
 */


import { getGlobal } from './global';

/**
 * 是否支持 ErrorEvent 对象
 * @returns boolean
 */
export function supportsErrorEvent(): boolean {
    try {
        // tslint:disable:no-unused-expression
        new ErrorEvent('');
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * 是否支持 DOMException 对象
 * @returns boolean
 */
export function supportsDOMException(): boolean {
    try {
        // tslint:disable:no-unused-expression
        new DOMException('');
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * 是否支持 fetch 对象
 * @returns boolean
 */
export function supportsFetch(): boolean {
    if (!('fetch' in getGlobal())) {
        return false;
    }

    try {
        // tslint:disable-next-line:no-unused-expression
        new Headers();
        // tslint:disable-next-line:no-unused-expression
        new Request('');
        // tslint:disable-next-line:no-unused-expression
        new Response();
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * 是否支持 native fetch 对象（而不是第三方 polyfill）
 * @returns boolean
 */
export function supportsNativeFetch(): boolean {
    if (!supportsFetch()) {
        return false;
    }
    const global = getGlobal();
    const fetch = (global as any).fetch;
    // tslint:disable-next-line:no-unsafe-any
    return fetch.toString().indexOf('native') !== -1;
}

/**
 * 是否支持 Beacon 对象(IE不支持)，可以通过 HTTP 将少量数据异步传输到服务器（即使浏览器关闭了）
 * see {@link https://developer.mozilla.org/zh-CN/docs/Web/API/Navigator/sendBeacon}
 * @returns boolean
 */
export function supportsBeacon(): boolean {
    const global = getGlobal();
    return 'navigator' in global && 'sendBeacon' in global.navigator;
}

/**
 * 是否支持 ReferrerPolicy 对象
 * @returns boolean
 */
export function supportsReferrerPolicy(): boolean {
    // Despite all stars in the sky saying that Edge supports old draft syntax,
    // aka 'never', 'always', 'origin' and 'default
    // https://caniuse.com/#feat=referrer-policy
    // It doesn't. And it throw exception instead of ignoring this parameter...
    // REF: https://github.com/getsentry/raven-js/issues/1233

    if (!supportsFetch()) {
        return false;
    }

    try {
        // tslint:disable:no-unused-expression
        new Request('pickleRick', {
            referrerPolicy: 'origin' as ReferrerPolicy,
        });
        return true;
    } catch (e) {
        return false;
    }
}

/**
 * 是否支持 history API 对象
 * @returns boolean
 */
export function supportsHistory(): boolean {
    // NOTE: in Chrome App environment, touching history.pushState, *even inside
    //       a try/catch block*, will cause Chrome to output an error to console.error
    // borrowed from: https://github.com/angular/angular.js/pull/13945/files
    const global = getGlobal();
    const chrome = (global as any).chrome;
    // tslint:disable-next-line:no-unsafe-any
    const isChromePackagedApp = chrome && chrome.app && chrome.app.runtime;
    const hasHistoryApi = 'history' in global && !!global.history.pushState && !!global.history.replaceState;

    return !isChromePackagedApp && hasHistoryApi;
}

/**
 * 是否支持 URL 和 URLSearchParams 对象
 * @return boolean
 */
export function supportsURL(): boolean {
    const global = getGlobal();
    return ('URL' in global) && ('URLSearchParams' in global);
}


/**
 * 是否支持 Document 对象
 * @return boolean
 */
export function supportsDocument(): boolean {
    const global = getGlobal();
    return ('document' in global);
}
